import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _Branch;
import RopeSequence from 'rope-sequence';
import { Plugin, PluginKey } from '@atlaskit/editor-prosemirror/state';
import { Mapping } from '@atlaskit/editor-prosemirror/transform';

// Duplicated type to avoid circular dependency

// ProseMirror's history isn't simply a way to roll back to a previous
// state, because ProseMirror supports applying changes without adding
// them to the history (for example during collaboration).
//
// To this end, each 'Branch' (one for the undo history and one for
// the redo history) keeps an array of 'Items', which can optionally
// hold a step (an actual undoable change), and always hold a position
// map (which is needed to move changes below them to apply to the
// current document).
//
// An item that has both a step and a selection bookmark is the start
// of an 'event' — a group of changes that will be undone or redone at
// once. (It stores only the bookmark, since that way we don't have to
// provide a document until the selection is actually applied, which
// is useful when compressing.)

// Used to schedule history compression
var max_empty_items = 500;
var Branch = /*#__PURE__*/function () {
  function Branch(items, eventCount) {
    _classCallCheck(this, Branch);
    this.items = items;
    this.eventCount = eventCount;
  }

  // Pop the latest event off the branch's history and apply it
  // to a document transform.
  return _createClass(Branch, [{
    key: "popEvent",
    value: function popEvent(state, preserveItems) {
      var _this = this;
      // To match existing behaviour of prosemirror-history
      // eslint-disable-next-line eqeqeq
      if (this.eventCount == 0) {
        return null;
      }
      var end = this.items.length;
      for (;; end--) {
        var next = this.items.get(end - 1);
        if (next.selection) {
          --end;
          break;
        }
      }
      var remap, mapFrom;
      if (preserveItems) {
        remap = this.remapping(end, this.items.length);
        mapFrom = remap.maps.length;
      }
      var transform = state.tr;
      var selection, remaining;
      var addAfter = [],
        addBefore = [];
      this.items.forEach(function (item, i) {
        if (!item.step) {
          if (!remap) {
            remap = _this.remapping(end, i + 1);
            mapFrom = remap.maps.length;
          }
          // To match existing behaviour of prosemirror-history
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          mapFrom--;
          addBefore.push(item);
          return;
        }
        if (remap) {
          addBefore.push(new Item(item.map));
          var _step = item.step.map(remap.slice(mapFrom));
          var _map;
          if (_step && transform.maybeStep(_step).doc) {
            _map = transform.mapping.maps[transform.mapping.maps.length - 1];
            addAfter.push(new Item(_map, undefined, undefined, addAfter.length + addBefore.length));
          }
          // To match existing behaviour of prosemirror-history
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          mapFrom--;
          if (_map) {
            remap.appendMap(_map, mapFrom);
          }
        } else {
          transform.maybeStep(item.step);
        }
        if (item.selection) {
          selection = remap ? item.selection.map(remap.slice(mapFrom)) : item.selection;
          remaining = new Branch(_this.items.slice(0, end).append(addBefore.reverse().concat(addAfter)), _this.eventCount - 1);
          return false;
        }
      }, this.items.length, 0);

      // To match existing behaviour of prosemirror-history
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return {
        remaining: remaining,
        transform: transform,
        selection: selection
      };
    }

    // Create a new branch with the given transform added.
  }, {
    key: "addTransform",
    value: function addTransform(transform, selection, histOptions, preserveItems) {
      var newItems = [];
      var eventCount = this.eventCount;
      var oldItems = this.items,
        lastItem = !preserveItems && oldItems.length ? oldItems.get(oldItems.length - 1) : null;
      for (var i = 0; i < transform.steps.length; i++) {
        var _step2 = transform.steps[i].invert(transform.docs[i]);
        var item = new Item(transform.mapping.maps[i], _step2, selection),
          merged = void 0;
        // To match existing behaviour of prosemirror-history
        // eslint-disable-next-line no-cond-assign
        if (merged = lastItem && lastItem.merge(item)) {
          item = merged;
          if (i) {
            newItems.pop();
          } else {
            oldItems = oldItems.slice(0, oldItems.length - 1);
          }
        }
        newItems.push(item);
        if (selection) {
          eventCount++;
          selection = undefined;
        }
        if (!preserveItems) {
          lastItem = item;
        }
      }
      var overflow = eventCount - histOptions.depth;
      if (overflow > DEPTH_OVERFLOW) {
        oldItems = cutOffEvents(oldItems, overflow);
        eventCount -= overflow;
      }
      return new Branch(oldItems.append(newItems), eventCount);
    }
  }, {
    key: "remapping",
    value: function remapping(from, to) {
      var maps = new Mapping();
      this.items.forEach(function (item, i) {
        var mirrorPos = item.mirrorOffset != null && i - item.mirrorOffset >= from ? maps.maps.length - item.mirrorOffset : undefined;
        maps.appendMap(item.map, mirrorPos);
      }, from, to);
      return maps;
    }
  }, {
    key: "addMaps",
    value: function addMaps(array) {
      // To match existing behaviour of prosemirror-history
      // eslint-disable-next-line eqeqeq
      if (this.eventCount == 0) {
        return this;
      }
      return new Branch(this.items.append(array.map(function (map) {
        return new Item(map);
      })), this.eventCount);
    }

    // When the collab module receives remote changes, the history has
    // to know about those, so that it can adjust the steps that were
    // rebased on top of the remote changes, and include the position
    // maps for the remote changes in its array of items.
  }, {
    key: "rebased",
    value: function rebased(rebasedTransform, rebasedCount) {
      if (!this.eventCount) {
        return this;
      }
      var rebasedItems = [],
        start = Math.max(0, this.items.length - rebasedCount);
      var mapping = rebasedTransform.mapping;
      var newUntil = rebasedTransform.steps.length;
      var eventCount = this.eventCount;
      this.items.forEach(function (item) {
        if (item.selection) {
          eventCount--;
        }
      }, start);
      var iRebased = rebasedCount;
      this.items.forEach(function (item) {
        var pos = mapping.getMirror(--iRebased);
        // To match existing behaviour of prosemirror-history
        // eslint-disable-next-line eqeqeq
        if (pos == null) {
          return;
        }
        newUntil = Math.min(newUntil, pos);
        var map = mapping.maps[pos];
        if (item.step) {
          var _step3 = rebasedTransform.steps[pos].invert(rebasedTransform.docs[pos]);
          var _selection = item.selection && item.selection.map(mapping.slice(iRebased + 1, pos));
          if (_selection) {
            eventCount++;
          }
          rebasedItems.push(new Item(map, _step3, _selection));
        } else {
          rebasedItems.push(new Item(map));
        }
      }, start);
      var newMaps = [];
      for (var i = rebasedCount; i < newUntil; i++) {
        newMaps.push(new Item(mapping.maps[i]));
      }
      var items = this.items.slice(0, start).append(newMaps).append(rebasedItems);
      var branch = new Branch(items, eventCount);
      if (branch.emptyItemCount() > max_empty_items) {
        branch = branch.compress(this.items.length - rebasedItems.length);
      }
      return branch;
    }
  }, {
    key: "emptyItemCount",
    value: function emptyItemCount() {
      var count = 0;
      this.items.forEach(function (item) {
        if (!item.step) {
          count++;
        }
      });
      return count;
    }

    // Compressing a branch means rewriting it to push the air (map-only
    // items) out. During collaboration, these naturally accumulate
    // because each remote change adds one. The `upto` argument is used
    // to ensure that only the items below a given level are compressed,
    // because `rebased` relies on a clean, untouched set of items in
    // order to associate old items with rebased steps.
  }, {
    key: "compress",
    value: function compress() {
      var upto = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : this.items.length;
      var remap = this.remapping(0, upto);
      var mapFrom = remap.maps.length;
      var items = [];
      var events = 0;
      this.items.forEach(function (item, i) {
        if (i >= upto) {
          items.push(item);
          if (item.selection) {
            events++;
          }
        } else if (item.step) {
          var _step4 = item.step.map(remap.slice(mapFrom)),
            _map2 = _step4 && _step4.getMap();
          mapFrom--;
          if (_map2) {
            remap.appendMap(_map2, mapFrom);
          }
          if (_step4) {
            var _selection2 = item.selection && item.selection.map(remap.slice(mapFrom));
            if (_selection2) {
              events++;
            }
            // To match existing behaviour of prosemirror-history
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            var newItem = new Item(_map2.invert(), _step4, _selection2);
            var merged;
            var last = items.length - 1;
            // To match existing behaviour of prosemirror-history
            // eslint-disable-next-line no-cond-assign
            if (merged = items.length && items[last].merge(newItem)) {
              items[last] = merged;
            } else {
              items.push(newItem);
            }
          }
        } else if (item.map) {
          mapFrom--;
        }
      }, this.items.length, 0);
      return new Branch(RopeSequence.from(items.reverse()), events);
    }
  }]);
}();
_Branch = Branch;
_defineProperty(Branch, "empty", new _Branch(RopeSequence.empty, 0));
function cutOffEvents(items, n) {
  var cutPoint;
  items.forEach(function (item, i) {
    // To match existing behaviour of prosemirror-history
    // eslint-disable-next-line eqeqeq
    if (item.selection && n-- == 0) {
      cutPoint = i;
      return false;
    }
  });
  // To match existing behaviour of prosemirror-history
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return items.slice(cutPoint);
}
var Item = /*#__PURE__*/function () {
  function Item(
  // The (forward) step map for this item.
  map,
  // The inverted step
  step,
  // If this is non-null, this item is the start of a group, and
  // this selection is the starting selection for the group (the one
  // that was active before the first step was applied)
  selection,
  // If this item is the inverse of a previous mapping on the stack,
  // this points at the inverse's offset
  mirrorOffset) {
    _classCallCheck(this, Item);
    this.map = map;
    this.step = step;
    this.selection = selection;
    this.mirrorOffset = mirrorOffset;
  }
  return _createClass(Item, [{
    key: "merge",
    value: function merge(other) {
      if (this.step && other.step && !other.selection) {
        var _step5 = other.step.merge(this.step);
        if (_step5) {
          return new Item(_step5.getMap().invert(), _step5, this.selection);
        }
      }
    }
  }]);
}(); // The value of the state field that tracks undo/redo history for that
// state. Will be stored in the plugin state when the history plugin
// is active.
var HistoryState = /*#__PURE__*/_createClass(function HistoryState(done, undone, prevRanges, prevTime, prevComposition) {
  _classCallCheck(this, HistoryState);
  this.done = done;
  this.undone = undone;
  this.prevRanges = prevRanges;
  this.prevTime = prevTime;
  this.prevComposition = prevComposition;
});
var DEPTH_OVERFLOW = 20;

// Record a transformation in undo history.
function applyTransaction(history, state, tr, options) {
  var historyTr = tr.getMeta(historyKey);
  var rebased;
  if (historyTr) {
    return historyTr.historyState;
  }
  if (tr.getMeta(closeHistoryKey)) {
    history = new HistoryState(history.done, history.undone, null, 0, -1);
  }
  var appended = tr.getMeta('appendedTransaction');

  // To match existing behaviour of prosemirror-history
  // eslint-disable-next-line eqeqeq
  if (tr.steps.length == 0) {
    return history;
  } else if (appended && appended.getMeta(historyKey)) {
    if (appended.getMeta(historyKey).redo) {
      return new HistoryState(history.done.addTransform(tr, undefined, options, mustPreserveItems(state)), history.undone, rangesFor(tr.mapping.maps), history.prevTime, history.prevComposition);
    } else {
      return new HistoryState(history.done, history.undone.addTransform(tr, undefined, options, mustPreserveItems(state)), null, history.prevTime, history.prevComposition);
    }
  } else if (tr.getMeta('addToHistory') !== false && !(appended && appended.getMeta('addToHistory') === false)) {
    // Group transforms that occur in quick succession into one event.
    var composition = tr.getMeta('composition');
    var newGroup =
    // To match existing behaviour of prosemirror-history
    // eslint-disable-next-line eqeqeq
    history.prevTime == 0 || !appended &&
    // To match existing behaviour of prosemirror-history
    // eslint-disable-next-line eqeqeq
    history.prevComposition != composition && (history.prevTime < (tr.time || 0) - options.newGroupDelay ||
    // To match existing behaviour of prosemirror-history
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    !isAdjacentTo(tr, history.prevRanges));
    var _prevRanges = appended ?
    // To match existing behaviour of prosemirror-history
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    mapRanges(history.prevRanges, tr.mapping) : rangesFor(tr.mapping.maps);
    return new HistoryState(history.done.addTransform(tr, newGroup ? state.selection.getBookmark() : undefined, options, mustPreserveItems(state)), Branch.empty, _prevRanges, tr.time,
    // To match existing behaviour of prosemirror-history
    // eslint-disable-next-line eqeqeq
    composition == null ? history.prevComposition : composition);
    // To match existing behaviour of prosemirror-history
    // eslint-disable-next-line no-cond-assign
  } else if (rebased = tr.getMeta('rebased')) {
    // Used by the collab module to tell the history that some of its
    // content has been rebased.
    return new HistoryState(history.done.rebased(tr, rebased), history.undone.rebased(tr, rebased),
    // To match existing behaviour of prosemirror-history
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    mapRanges(history.prevRanges, tr.mapping), history.prevTime, history.prevComposition);
  } else {
    return new HistoryState(history.done.addMaps(tr.mapping.maps), history.undone.addMaps(tr.mapping.maps),
    // To match existing behaviour of prosemirror-history
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    mapRanges(history.prevRanges, tr.mapping), history.prevTime, history.prevComposition);
  }
}
function isAdjacentTo(transform, prevRanges) {
  if (!prevRanges) {
    return false;
  }
  if (!transform.docChanged) {
    return true;
  }
  var adjacent = false;
  transform.mapping.maps[0].forEach(function (start, end) {
    for (var i = 0; i < prevRanges.length; i += 2) {
      if (start <= prevRanges[i + 1] && end >= prevRanges[i]) {
        adjacent = true;
      }
    }
  });
  return adjacent;
}
function rangesFor(maps) {
  var result = [];
  // To match existing behaviour of prosemirror-history
  // eslint-disable-next-line eqeqeq
  for (var i = maps.length - 1; i >= 0 && result.length == 0; i--) {
    maps[i].forEach(function (_from, _to, from, to) {
      return result.push(from, to);
    });
  }
  return result;
}
function mapRanges(ranges, mapping) {
  if (!ranges) {
    return null;
  }
  var result = [];
  for (var i = 0; i < ranges.length; i += 2) {
    var from = mapping.map(ranges[i], 1),
      to = mapping.map(ranges[i + 1], -1);
    if (from <= to) {
      result.push(from, to);
    }
  }
  return result;
}

// Apply the latest event from one branch to the document and shift the event
// onto the other branch.
function histTransaction(history, state, redo) {
  var preserveItems = mustPreserveItems(state);
  // To match existing behaviour of prosemirror-history
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-explicit-any
  var histOptions = historyKey.get(state).spec.config;
  var pop = (redo ? history.undone : history.done).popEvent(state, preserveItems);
  if (!pop) {
    return null;
  }

  // To match existing behaviour of prosemirror-history
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  var selection = pop.selection.resolve(pop.transform.doc);
  var added = (redo ? history.done : history.undone).addTransform(pop.transform, state.selection.getBookmark(), histOptions, preserveItems);
  var newHist = new HistoryState(redo ? added : pop.remaining, redo ? pop.remaining : added, null, 0, -1);
  return pop.transform.setSelection(selection).setMeta(historyKey, {
    redo: redo,
    historyState: newHist
  });
}
var cachedPreserveItems = false,
  cachedPreserveItemsPlugins = null;
// Check whether any plugin in the given state has a
// `historyPreserveItems` property in its spec, in which case we must
// preserve steps exactly as they came in, so that they can be
// rebased.
function mustPreserveItems(state) {
  var plugins = state.plugins;
  // To match existing behaviour of prosemirror-history
  // eslint-disable-next-line eqeqeq
  if (cachedPreserveItemsPlugins != plugins) {
    cachedPreserveItems = false;
    cachedPreserveItemsPlugins = plugins;
    for (var i = 0; i < plugins.length; i++) {
      // To match existing behaviour of prosemirror-history
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (plugins[i].spec.historyPreserveItems) {
        cachedPreserveItems = true;
        break;
      }
    }
  }
  return cachedPreserveItems;
}

/// Set a flag on the given transaction that will prevent further steps
/// from being appended to an existing history event (so that they
/// require a separate undo command to undo).
export function closeHistory(tr) {
  return tr.setMeta(closeHistoryKey, true);
}
var historyKey = new PluginKey('history');
var closeHistoryKey = new PluginKey('closeHistory');
/// Returns a plugin that enables the undo history for an editor. The
/// plugin will track undo and redo stacks, which can be used with the
/// [`undo`](#history.undo) and [`redo`](#history.redo) commands.
///
/// You can set an `"addToHistory"` [metadata
/// property](#state.Transaction.setMeta) of `false` on a transaction
/// to prevent it from being rolled back by undo.
export function history() {
  var config = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  config = {
    depth: config.depth || 100,
    newGroupDelay: config.newGroupDelay || 500
  };
  return new Plugin({
    key: historyKey,
    state: {
      init: function init() {
        return new HistoryState(Branch.empty, Branch.empty, null, 0, -1);
      },
      apply: function apply(tr, hist, state) {
        return applyTransaction(hist, state, tr, config);
      }
    },
    config: config,
    props: {
      handleDOMEvents: {
        beforeinput: function beforeinput(view, e) {
          var inputType = e.inputType;
          var command =
          // To match existing behaviour of prosemirror-history
          // eslint-disable-next-line eqeqeq
          inputType == 'historyUndo' ? undo : inputType == 'historyRedo' ? redo : null;
          if (!command || !view.editable) {
            return false;
          }
          e.preventDefault();
          return command(view.state, view.dispatch);
        }
      }
    }
  });
}
function buildCommand(redo, scroll) {
  return function (state, dispatch) {
    var hist = historyKey.getState(state);
    // To match existing behaviour of prosemirror-history
    // eslint-disable-next-line eqeqeq
    if (!hist || (redo ? hist.undone : hist.done).eventCount == 0) {
      return false;
    }
    if (dispatch) {
      var _tr = histTransaction(hist, state, redo);
      if (_tr) {
        dispatch(scroll ? _tr.scrollIntoView() : _tr);
      }
    }
    return true;
  };
}

/// A command function that undoes the last change, if any.
export var undo = buildCommand(false, true);

/// A command function that redoes the last undone change, if any.
export var redo = buildCommand(true, true);

/// A command function that undoes the last change. Don't scroll the
/// selection into view.
export var undoNoScroll = buildCommand(false, false);

/// A command function that redoes the last undone change. Don't
/// scroll the selection into view.
export var redoNoScroll = buildCommand(true, false);

/// The amount of undoable events available in a given state.
export function undoDepth(state) {
  var hist = historyKey.getState(state);
  return hist ? hist.done.eventCount : 0;
}

/// The amount of redoable events available in a given editor state.
export function redoDepth(state) {
  var hist = historyKey.getState(state);
  return hist ? hist.undone.eventCount : 0;
}